import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DataTable from "../../components/table/data-table";
import { DeleteItem, InviteModal, SuccessModal, SuccessfulDelete } from "../../components/modal";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import DeleteImg from "../../assets/delLogo.png";
import verifyImg from "../../assets/verifyImg.png";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const LeaveFlow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const apiBaseUrl = getApiBaseUrl();
  const approvalFlowId = useMemo(
    () => location.state?.approvalFlowId || sessionStorage.getItem("selectedApprovalFlowId"),
    [location.state],
  );
  const cacheKey = useMemo(() => `cehr_leave_flow_${approvalFlowId || "unknown"}`, [approvalFlowId]);
  const cachedData = useMemo(
    () => readSessionCache(cacheKey, { approvalFlow: null, steps: [] }),
    [cacheKey],
  );

  const [approvalFlow, setApprovalFlow] = useState(cachedData?.approvalFlow || null);
  const [steps, setSteps] = useState(cachedData?.steps || []);
  const [levels, setLevels] = useState([]);
  const [positions, setPositions] = useState([]);
  const [openCreateStepModal, setOpenCreateStepModal] = useState(false);
  const [openEditFlowModal, setOpenEditFlowModal] = useState(false);
  const [openDeleteFlowModal, setOpenDeleteFlowModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openDeleteSuccessModal, setOpenDeleteSuccessModal] = useState(false);
  const [flowName, setFlowName] = useState("");
  const [flowDescription, setFlowDescription] = useState("");
  const [levelId, setLevelId] = useState("");
  const [selectedPositionIds, setSelectedPositionIds] = useState([]);
  const [draggedPositionId, setDraggedPositionId] = useState("");
  const [isLoading, setIsLoading] = useState(!cachedData?.approvalFlow && !(cachedData?.steps?.length));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loadLookupData = useCallback(async () => {
    if (!apiBaseUrl) return;

    const [levelsResponse, positionsResponse] = await Promise.all([
      axios.get(`${apiBaseUrl}/organizations/levels`),
      axios.get(`${apiBaseUrl}/organizations/positions`),
    ]);

    setLevels(levelsResponse?.data?.levels || []);
    setPositions(positionsResponse?.data?.positions || []);
  }, [apiBaseUrl]);

  const loadApprovalFlow = useCallback(async ({ silent = false } = {}) => {
    if (!apiBaseUrl || !approvalFlowId) {
      setErrorMessage(!approvalFlowId ? "Select an approval flow first." : "API base URL is not configured.");
      return;
    }

    try {
      if (!silent && !approvalFlow && steps.length === 0) {
        setIsLoading(true);
      }
      setErrorMessage("");

      const [flowResponse, stepsResponse] = await Promise.all([
        axios.get(`${apiBaseUrl}/organizations/approval-flows/${approvalFlowId}`),
        axios.get(`${apiBaseUrl}/organizations/approval-flows/${approvalFlowId}/steps`),
      ]);

      const loadedFlow = flowResponse?.data?.approvalFlow || null;
      const loadedSteps = stepsResponse?.data?.steps || [];
      const mappedSteps = loadedSteps.map((step) => {
        const approverCount = step?.approvers?.length || 0;

        return {
          ...step,
          approverSummary: (
            <div className="flex items-center gap-2">
              <span>{step.approverNames || "-"}</span>
              <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-[#E9EEFF] text-[#1234CD] text-xs font-semibold">
                {approverCount}
              </span>
            </div>
          ),
        };
      });

      setApprovalFlow(loadedFlow);
      setFlowName(loadedFlow?.approvalName || "");
      setFlowDescription(loadedFlow?.approvalDescription || "");
      setSteps(mappedSteps);
      writeSessionCache(cacheKey, { approvalFlow: loadedFlow, steps: mappedSteps });
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to load approval flow details. Please try again.";
      setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, approvalFlow, approvalFlowId, cacheKey, steps.length]);

  useEffect(() => {
    loadApprovalFlow({ silent: true });
    loadLookupData();
  }, [approvalFlowId, loadApprovalFlow, loadLookupData]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadApprovalFlow({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadApprovalFlow]);

  const openCreateStep = () => {
    setLevelId("");
    setSelectedPositionIds([]);
    setDraggedPositionId("");
    setErrorMessage("");
    setOpenCreateStepModal(true);
  };

  const togglePositionSelection = (positionId) => {
    setSelectedPositionIds((current) =>
      current.includes(positionId)
        ? current.filter((id) => id !== positionId)
        : [...current, positionId],
    );
  };

  const getPositionSelectionOrder = (positionId) => {
    const order = selectedPositionIds.indexOf(positionId);
    return order >= 0 ? order + 1 : null;
  };

  const getPositionName = (positionId) => {
    const matched = positions.find((position) => position.id === positionId);
    return matched?.positionName || "Unknown position";
  };

  const handleSelectedDragStart = (positionId) => {
    setDraggedPositionId(positionId);
  };

  const handleSelectedDrop = (targetPositionId) => {
    if (!draggedPositionId || draggedPositionId === targetPositionId) {
      setDraggedPositionId("");
      return;
    }

    setSelectedPositionIds((current) => {
      const next = [...current];
      const fromIndex = next.indexOf(draggedPositionId);
      const toIndex = next.indexOf(targetPositionId);

      if (fromIndex < 0 || toIndex < 0) {
        return current;
      }

      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });

    setDraggedPositionId("");
  };

  const handleCreateStep = async (ev) => {
    ev.preventDefault();

    if (!apiBaseUrl || !approvalFlowId) {
      setErrorMessage("Unable to create flow step right now.");
      return;
    }

    if (!levelId || selectedPositionIds.length === 0) {
      setErrorMessage("Please select a level and at least one approver position.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      await axios.post(`${apiBaseUrl}/organizations/approval-flows/${approvalFlowId}/steps`, {
        levelId,
        positionIds: selectedPositionIds,
      });

      setOpenCreateStepModal(false);
      setOpenSuccessModal(true);
      await loadApprovalFlow();
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to create flow step. Please try again.";
      setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateFlow = async (ev) => {
    ev.preventDefault();

    if (!apiBaseUrl || !approvalFlowId) {
      setErrorMessage("Unable to update approval flow right now.");
      return;
    }

    if (!flowName.trim() || !flowDescription.trim()) {
      setErrorMessage("Approval name and approval description are required.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      await axios.patch(`${apiBaseUrl}/organizations/approval-flows/${approvalFlowId}`, {
        approvalName: flowName.trim(),
        approvalDescription: flowDescription.trim(),
      });

      setOpenEditFlowModal(false);
      setOpenSuccessModal(true);
      await loadApprovalFlow();
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to update approval flow. Please try again.";
      setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteFlow = async () => {
    if (!apiBaseUrl || !approvalFlowId) {
      setErrorMessage("Unable to delete approval flow right now.");
      return;
    }

    try {
      setIsDeleting(true);
      setErrorMessage("");

      await axios.delete(`${apiBaseUrl}/organizations/approval-flows/${approvalFlowId}`);

      setOpenDeleteFlowModal(false);
      setOpenDeleteSuccessModal(true);
      sessionStorage.removeItem("selectedApprovalFlowId");

      setTimeout(() => {
        navigate("/company-settings/approval-flow");
      }, 1200);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to delete approval flow. Please try again.";
      setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const headings = ["Level", "Approvers", "Actions"];
  const columns = ["levelName", "approverSummary"];

  return (
    <div>
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Approval Flows", path: "/company-settings/approval-flow" },
          { label: approvalFlow?.approvalName || "Flow Details" },
        ]}
        title={approvalFlow?.approvalName || "Approval Flow"}
        subtitle="Configure levels and approver positions for this flow."
        fallbackPath="/company-settings/approval-flow"
        backLabel="Back to Approval Flows"
        actions={(
          <div className="flex items-center gap-3">
            <button
              onClick={() => setOpenEditFlowModal(true)}
              className="border border-[#1234CD] text-[#1234CD] px-4 py-2 rounded-xl"
            >
              Edit flow
            </button>
            <button
              onClick={() => setOpenDeleteFlowModal(true)}
              className="border border-red-500 text-red-600 px-4 py-2 rounded-xl"
            >
              Delete flow
            </button>
          </div>
        )}
      />

      {errorMessage && (
        <p className="px-6 mb-4 text-sm text-red-600" role="alert">
          {errorMessage}
        </p>
      )}

      <DataTable
        title="Flow Levels"
        headings={headings}
        columns={columns}
        data={steps}
        onAddNew={openCreateStep}
        showEmptyState={!isLoading}
        emptyMessage="No flow levels added yet. Add one to get started."
        onView={(row) => {
          sessionStorage.setItem("selectedApprovalFlowStepId", row.id);
          sessionStorage.setItem("selectedApprovalFlowId", approvalFlowId || "");
          navigate("/company-settings/leave-flow-details", {
            state: { approvalFlowId, stepId: row.id },
          });
        }}
      />

      {isLoading && steps.length === 0 ? <FetchLoadingState message="Loading flow levels..." /> : null}

      <InviteModal isOpen={openCreateStepModal} onClose={() => setOpenCreateStepModal(false)}>
        <div className="my-8 font-Outfit">
          <p className="text-2xl font-semibold text-black">Add Flow Level</p>
          <form className="my-8" onSubmit={handleCreateStep}>
            <div>
              <label className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px]">Level</label>
              <select
                value={levelId}
                onChange={(ev) => setLevelId(ev.target.value)}
                className="w-full p-3 mb-4 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5]"
              >
                <option value="">Select level</option>
                {levels.map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.levelName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px]">Approvers (Positions)</label>
              <div className="max-h-44 overflow-y-auto border-2 border-[#F3F5F5] rounded-xl p-3">
                {positions.map((position) => (
                  <label key={position.id} className="flex items-center justify-between gap-2 py-1 text-sm text-black">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedPositionIds.includes(position.id)}
                        onChange={() => togglePositionSelection(position.id)}
                      />
                      <span>{position.positionName}</span>
                    </div>
                    {getPositionSelectionOrder(position.id) ? (
                      <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 rounded-full bg-[#E9EEFF] text-[#1234CD] text-xs font-semibold">
                        {getPositionSelectionOrder(position.id)}
                      </span>
                    ) : null}
                  </label>
                ))}
              </div>
              {selectedPositionIds.length > 0 ? (
                <p className="mt-2 text-xs text-[#5F6B8A]">
                  Selection order is shown with badges and will be saved as selected.
                </p>
              ) : null}

              {selectedPositionIds.length > 1 ? (
                <div className="mt-3 rounded-xl border border-[#E9EEFF] p-3">
                  <p className="mb-2 text-xs font-medium text-[#5F6B8A]">Drag to reorder selected approvers</p>
                  <div className="space-y-2">
                    {selectedPositionIds.map((positionId, index) => (
                      <div
                        key={positionId}
                        draggable
                        onDragStart={() => handleSelectedDragStart(positionId)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => handleSelectedDrop(positionId)}
                        onDragEnd={() => setDraggedPositionId("")}
                        className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm cursor-move ${
                          draggedPositionId === positionId ? "border-[#1234CD] bg-[#F3F6FF]" : "border-[#EEF1FB] bg-white"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 rounded-full bg-[#E9EEFF] text-[#1234CD] text-xs font-semibold">
                            {index + 1}
                          </span>
                          <span>{getPositionName(positionId)}</span>
                        </div>
                        <span className="text-xs text-gray-400">Drag</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex justify-center mt-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className="font-[700] leading-[21.6px] w-40 rounded-xl text-white py-[12px] bg-[#1234CD] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Creating..." : "Create"}
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <InviteModal isOpen={openEditFlowModal} onClose={() => setOpenEditFlowModal(false)}>
        <div className="my-8 font-Outfit">
          <p className="text-2xl font-semibold text-black">Edit Approval Flow</p>
          <form className="my-8" onSubmit={handleUpdateFlow}>
            <div>
              <label className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px]">Approval Name</label>
              <input
                type="text"
                value={flowName}
                onChange={(ev) => setFlowName(ev.target.value)}
                className="w-full p-3 mb-4 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5]"
              />
            </div>
            <div>
              <label className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px]">Approval Description</label>
              <textarea
                value={flowDescription}
                onChange={(ev) => setFlowDescription(ev.target.value)}
                rows={4}
                wrap="soft"
                className="w-full p-3 min-h-24 mb-4 resize-y whitespace-pre-wrap break-words rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5]"
              />
            </div>
            <div className="flex justify-center mt-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className="font-[700] leading-[21.6px] w-40 rounded-xl text-white py-[12px] bg-[#1234CD] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Updating..." : "Update"}
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <DeleteItem isOpen={openDeleteFlowModal} onClose={() => setOpenDeleteFlowModal(false)}>
        <div>
          <img className="w-40 mx-auto animate-pulse" src={DeleteImg} alt="delete approval flow" />
          <h1 className="text-lg text-center text-[#000000] font-bold">Continue to delete this approval flow?</h1>
          <p className="text-sm text-center mt-2">Action cannot be undone.</p>
        </div>
        <div className="flex items-center py-4 gap-4 my-4">
          <button
            onClick={() => setOpenDeleteFlowModal(false)}
            className="border-[#1233CC] py-1 border-2 text-[#1233CC] font-medium px-4 rounded-[10px] w-[102px]"
          >
            No
          </button>
          <button
            className="text-[16px] font-medium px-4 py-1 bg-[#1233CC] rounded-[10px] w-[102px] text-white disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={handleDeleteFlow}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Yes"}
          </button>
        </div>
      </DeleteItem>

      <SuccessModal isOpen={openSuccessModal} onClose={() => setOpenSuccessModal(false)} />

      <SuccessfulDelete isOpen={openDeleteSuccessModal} onClose={() => setOpenDeleteSuccessModal(false)}>
        <div>
          <img src={verifyImg} alt="approval flow deleted" className="w-20 animate-ping mx-auto" />
          <div className="text-center mt-6">
            <h2 className="text-black text-[20px] leading-[27px] font-bold mb-2">Approval flow deleted</h2>
            <p className="text-[#74777B] text-[14px] leading-[18.9px]">The approval flow was removed successfully.</p>
          </div>
        </div>
      </SuccessfulDelete>
    </div>
  );
};

export default LeaveFlow;
